/**
 * Pure ortho.zoom ⇄ pixels-per-world conversion helpers.
 *
 * Extracted from `cameraApiBinder.tsx` so a framework-light module
 * (`stageInteractions.ts`) can share the SAME canonical conversion the
 * R3F snapshot bridge uses WITHOUT pulling React / `@react-three/fiber`
 * into its import graph. `cameraApiBinder.tsx` re-exports both names for
 * back-compat (existing callers + tests keep their import paths).
 *
 * # Coordinate convention
 *
 * `CameraStateSnapshot.zoom` (== `cameraStateRef.current.zoom`) is **CSS
 * pixels per world unit**. The THREE OrthographicCamera's `.zoom`
 * property is a multiplier on the frustum, so the conversion is:
 *
 *   pixelsPerWorld = ortho.zoom * size.width / (ortho.right - ortho.left)
 *
 * The frustum width `(right - left)` is treated as a fixed-on-fit
 * quantity (recomputed only on fit-view); subsequent zoom mutations
 * adjust `ortho.zoom`, not the frustum extents.
 */

import * as THREE from "three";

/**
 * Read the live "pixels per world unit" zoom factor from a THREE
 * OrthographicCamera + canvas size. Returns a positive finite number;
 * collapses degenerate inputs to `1` to avoid poisoning downstream
 * math (callers store this into `cameraStateRef` which the bounds-fit
 * code divides into).
 */
export function pixelsPerWorldFromOrtho(
  ortho: THREE.OrthographicCamera,
  size: { width: number; height: number },
): number {
  const frustumW = ortho.right - ortho.left;
  if (!Number.isFinite(frustumW) || frustumW <= 0) return 1;
  if (size.width <= 0) return 1;
  const value = (ortho.zoom * size.width) / frustumW;
  if (!Number.isFinite(value) || value <= 0) return 1;
  return value;
}

/**
 * Convert a `cameraStateRef.zoom` (pixels per world unit) back into
 * the THREE OrthographicCamera's `.zoom` multiplier, given the
 * camera's current frustum + canvas size. Pure inverse of
 * `pixelsPerWorldFromOrtho`.
 */
export function orthoZoomFromPixelsPerWorld(
  pixelsPerWorld: number,
  ortho: THREE.OrthographicCamera,
  size: { width: number; height: number },
): number {
  const frustumW = ortho.right - ortho.left;
  if (!Number.isFinite(frustumW) || frustumW <= 0) return 1;
  if (size.width <= 0) return 1;
  const value = (pixelsPerWorld * frustumW) / size.width;
  if (!Number.isFinite(value) || value <= 0) return 1;
  return value;
}
